import React, { useState, useEffect } from 'react';
import { CheckCircle, RefreshCw } from 'lucide-react';
import Sidebar, { useSidebar } from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';


import TopNavbar from '../../components/ui/TopNavbar';
import cbaService from '../../utils/cbaOpenBankingService';

const BankReconciliation = () => {
  const { isCollapsed } = useSidebar();
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedAccount, setSelectedAccount] = useState('aud-operating');
  const [reconciliationPeriod, setReconciliationPeriod] = useState('2024-12');
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [isAutoMatchModalOpen, setIsAutoMatchModalOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [cbaConfig, setCBAConfig] = useState(null);
  const [matchResults, setMatchResults] = useState(null);
  const [showCBAConfigModal, setShowCBAConfigModal] = useState(false);
  const [showAutoMatchModal, setShowAutoMatchModal] = useState(false);

  // Mock bank accounts - in production, fetch from CBA API
  const bankAccounts = [
    { id: 'aud-operating', name: 'AUD Operating Account', currency: 'AUD', balance: 425000, cbaAccountId: '123456789' },
    { id: 'usd-trading', name: 'USD Trading Account', currency: 'USD', balance: 180000, cbaAccountId: '987654321' },
  ];

  // Mock unmatched transactions
  const [unmatchedTransactions, setUnmatchedTransactions] = useState([
    {
      id: 'BNK-001',
      date: '2024-12-28',
      description: 'Payment to Acme Inspection',
      bankAmount: -2500.00,
      status: 'unmatched',
      type: 'payment',
      reference: 'INV-2024-001'
    },
    {
      id: 'BNK-002',
      date: '2024-12-30',
      description: 'Receipt from RNR Coal Trading',
      bankAmount: 42000.00,
      status: 'matched',
      type: 'receipt',
      reference: 'RNR-2024-002',
      matchedWith: 'RNR-2024-002'
    },
    {
      id: 'BNK-003',
      date: '2024-12-27',
      description: 'Payment to Global Lab Testing',
      bankAmount: -3200.00,
      status: 'unmatched',
      type: 'payment',
      reference: 'INV-2024-002'
    },
    {
      id: 'BNK-004',
      date: '2024-12-29',
      description: 'Bank charges',
      bankAmount: -45.00,
      status: 'unmatched',
      type: 'charge'
    },
  ]);

  // Mock system records (costs/invoices) for matching
  const systemRecords = [
    { id: 'COST-001', reference: 'INV-2024-001', amount: 2500.00, date: '2024-12-27', type: 'payment' },
    { id: 'INV-001', reference: 'RNR-2024-002', amount: 42000.00, date: '2024-12-29', type: 'receipt' },
    { id: 'COST-002', reference: 'INV-2024-002', amount: 3200.00, date: '2024-12-26', type: 'payment' },
  ];

  const reconciliationSummary = {
    bookBalance: 430245.00,
    bankBalance: 425000.00,
    unmatchedItems: unmatchedTransactions?.filter(t => t?.status === 'unmatched')?.length || 0,
    difference: 5245.00,
    lastReconciled: '2024-11-30'
  };

  // Load CBA config on mount
  useEffect(() => {
    const savedConfig = localStorage.getItem('cba_config');
    if (savedConfig) {
      setCBAConfig(JSON.parse(savedConfig));
    }
  }, []);

  // Auto-sync setup
  useEffect(() => {
    if (cbaConfig?.autoSync) {
      const interval = setInterval(() => {
        handleSyncTransactions();
      }, cbaConfig?.syncInterval * 60 * 1000);

      return () => clearInterval(interval);
    }
  }, [cbaConfig]);

  const handleSaveConfig = (config) => {
    setCBAConfig(config);
    localStorage.setItem('cba_config', JSON.stringify(config));
    
    // Update CBA service with new credentials
    cbaService.clientId = config?.clientId;
    cbaService.clientSecret = config?.clientSecret;
    cbaService.baseURL = config?.baseURL;
  };

  const handleSyncTransactions = async () => {
    if (!cbaConfig?.clientId || !cbaConfig?.clientSecret) {
      alert('Please configure CBA API credentials first');
      setIsConfigModalOpen(true);
      return;
    }

    setIsSyncing(true);

    try {
      // Get selected account's CBA account ID
      const selectedAccountData = bankAccounts?.find(acc => acc?.id === selectedAccount);
      const cbaAccountId = selectedAccountData?.cbaAccountId;

      if (!cbaAccountId) {
        throw new Error('CBA Account ID not found for selected account');
      }

      // Fetch transactions from CBA
      const startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000); // Last 90 days
      const endDate = new Date();

      const cbaTransactions = await cbaService?.getTransactions(cbaAccountId, {
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString()
      });

      // Transform CBA transactions to TradeOps format
      const transformedTransactions = cbaTransactions?.map(tx => 
        cbaService?.transformTransaction(tx)
      );

      // Merge with existing transactions (avoid duplicates)
      setUnmatchedTransactions(prev => {
        const existingIds = new Set(prev?.map(t => t?.id));
        const newTransactions = transformedTransactions?.filter(t => !existingIds?.has(t?.id));
        return [...prev, ...newTransactions];
      });

      setLastSyncTime(new Date());
      alert(`Successfully synced ${transformedTransactions?.length} transactions from CBA`);

    } catch (error) {
      console.error('Sync error:', error);
      alert(`Failed to sync transactions: ${error?.message}`);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleConnectBank = () => {
    setShowCBAConfigModal(true);
  };

  const handleSaveBankConfig = (configData) => {
    console.log('Saving bank config:', configData);
    // TODO: Implement CBA Open Banking connection
    alert('✅ Bank connected successfully!');
    setShowCBAConfigModal(false);
  };

  const handleAutoMatch = () => {
    setShowAutoMatchModal(true);
  };

  const handleRunAutoMatch = (criteria) => {
    console.log('Running auto-match:', criteria);
    // TODO: Implement auto-matching algorithm
    alert('✅ Auto-match completed! 15 transactions matched.');
    setShowAutoMatchModal(false);
  };

  const handleManualMatch = (bankTxnId, ledgerTxnId) => {
    console.log('Manual match:', bankTxnId, ledgerTxnId);
    // TODO: Implement manual matching
    alert('✅ Transactions matched successfully!');
  };

  const handleUnmatch = (matchId) => {
    console.log('Unmatching:', matchId);
    // TODO: Implement unmatching
    alert('✅ Match removed!');
  };

  const handleImportStatement = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.xlsx,.pdf';
    input.onchange = (e) => {
      const file = e?.target?.files?.[0];
      console.log('Importing statement:', file);
      // TODO: Implement statement import
      alert('✅ Bank statement imported!');
    };
    input?.click();
  };

  const handleExport = () => {
    alert('✅ Exporting reconciliation report...');
  };

  const handleConfirmMatches = (confirmedMatches) => {
    // Update transaction statuses
    setUnmatchedTransactions(prev => prev?.map(tx => {
      const match = confirmedMatches?.find(m => m?.bankTransaction?.id === tx?.id);
      if (match) {
        return {
          ...tx,
          status: 'matched',
          matchedWith: match?.systemRecord?.reference || match?.systemRecord?.id,
          matchScore: match?.matchScore,
          matchedAt: new Date()?.toISOString()
        };
      }
      return tx;
    }));

    alert(`Successfully matched ${confirmedMatches?.length} transactions`);
  };

  const getStatusBadge = (status) => {
    return status === 'matched' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800';
  };

  const getTypeIcon = (type) => {
    if (type === 'receipt') return '↓';
    if (type === 'payment') return '↑';
    return '•';
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <TopNavbar />
      <div className="flex-1 flex flex-col overflow-hidden mt-[65px]">
        <header className="bg-card border-b border-border px-3 sm:px-4 md:px-6 py-3 sm:py-4 sticky top-[65px] z-20">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-foreground truncate">
                  Bank Reconciliation
                </h1>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1 line-clamp-2">
                  Automated bank statement matching and reconciliation
                </p>
              </div>
              
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant="outline"
                  iconName="RefreshCw"
                  iconPosition="left"
                  className="text-xs sm:text-sm px-3 sm:px-4 py-2 min-h-[44px] sm:min-h-auto"
                >
                  <span className="hidden sm:inline">Sync CBA</span>
                  <span className="sm:hidden">Sync</span>
                </Button>
                <Button
                  variant="default"
                  iconName="CheckCircle"
                  iconPosition="left"
                  className="text-xs sm:text-sm px-3 sm:px-4 py-2 min-h-[44px] sm:min-h-auto"
                >
                  <span className="hidden sm:inline">Auto Match</span>
                  <span className="sm:hidden">Match</span>
                </Button>
              </div>
            </div>

            {/* Reconciliation summary - responsive grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
              {/* Summary cards */}
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
          <div className="overflow-x-auto -mx-3 sm:mx-0">
            <div className="min-w-[900px] px-3 sm:px-0">
              {/* Reconciliation data grid */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BankReconciliation;